import React, { useState, useEffect, useMemo } from "react";
import ModuleDetail from "../common/ModuleDetail";
import DataApi from "../../api/dataApi";
import { COUNTRY_TIMEZONE } from "../../constants/COUNTRY_TIMEZONE";
import { useTimeZone } from "../../contexts/TimeZoneContext";
import { convertToUserTimezone } from "../../utils/convertTimeZone";

const UserDetail = ({ permissions }) => {
 const other = permissions?.allowEdit || permissions?.allowView
    ? {
      other: [
        { 
          key: "createdbyid", 
          label: "Created By", 
          type: "text",
          render: (value, rowData) => {
             // Checking exact DB names, camelCase, and snake_case
             return value || rowData?.createdbyid || rowData?.createdById || rowData?.created_by || rowData?.createdby || "—";
          }
        },
        {
          key: "createddate", 
          label: "Created Date", 
          type: "date",
          render: (value, rowData) => {
             // Checking exact DB names, camelCase, and snake_case
             const dateToConvert = value || rowData?.createddate || rowData?.createdDate || rowData?.created_at || rowData?.createdAt;
             return dateToConvert ? convertToUserTimezone(dateToConvert, timeZone) : "—";
          }
        },
        { 
          key: "lastmodifiedbyid", 
          label: "Last Modified By", 
          type: "text",
          render: (value, rowData) => {
            // Checking exact DB names, camelCase, and snake_case
            return value || rowData?.lastmodifiedbyid || rowData?.lastModifiedById || rowData?.modified_by || rowData?.updated_by || "—";
          }
        },
        {
          key: "lastmodifieddate", 
          label: "Last Modified Date", 
          type: "date",
          render: (value, rowData) => {
             // Notice rowData?.['lastmodifieddate '] catches the exact typo with the trailing space from your DB!
             const dateToConvert = value || rowData?.lastmodifieddate || rowData?.['lastmodifieddate '] || rowData?.lastModifiedDate || rowData?.updated_at || rowData?.updatedAt;
             return dateToConvert ? convertToUserTimezone(dateToConvert, timeZone) : "—";
          }
        },
      ],
    }
    : {};

  const [isLoading, setIsLoading] = useState(true);
  const { timeZone } = useTimeZone();

  const [currentCountry, setCurrentCountry] = useState(null);
  const { companyInfo } = useTimeZone();

  // ✅ VISUAL FIX: Instantly updates the locked text boxes on the screen!
  useEffect(() => {
    if (!currentCountry) return;
    const cData = COUNTRY_TIMEZONE.find(c => c.countryName === currentCountry);
    if (!cData) return;

    // Forcefully update the DOM inputs bypassing ModuleDetail's cached state
    const ccInput = document.querySelector('input[name="country_code"]') || document.getElementById('field-country_code');
    if (ccInput) ccInput.value = cData.phoneCode;

    const currInput = document.querySelector('input[name="currency"]') || document.getElementById('field-currency');
    if (currInput) currInput.value = cData.currency.code;

    const tzInput = document.querySelector('input[name="time_zone"]') || document.getElementById('field-time_zone');
    if (tzInput && cData.timezones?.length > 0) {
      tzInput.value = `${cData.timezones[0].zoneName} (${cData.timezones[0].gmtOffset})`;
    }
  }, [currentCountry]);

  const currencyOptions = useMemo(() => {
    if (!currentCountry) return [];
    const countryData = COUNTRY_TIMEZONE.find(ct => ct.countryName === currentCountry);
    if (countryData) {
      return [{
        value: countryData.currency.code,
        label: `${countryData.currency.code} - ${countryData.currency.name}`
      }];
    }
    return [];
  }, [currentCountry]);

  const timeZoneOptions = useMemo(() => {
    if (!currentCountry) return [];
    const countryData = COUNTRY_TIMEZONE.find(ct => ct.countryName === currentCountry);
    if (countryData) {
      return countryData.timezones.map(tz => ({
        value: tz.zoneName,
        label: `${tz.zoneName} (${tz.gmtOffset})`
      }));
    }
    return [];
  }, [currentCountry]);

  const [externalData, setExternalData] = useState({
    userRoles: [],
    companies: [],
  });

  const library_member_type = [
    { value: "Boys", label: "Male" },
    { value: "Girls", label: "Female" },
    { value: "Other", label: "Others" }
  ];

  useEffect(() => {
    const fetchExternalData = async () => {
      try {
        const roleApi = new DataApi("user-role");
        const companyApi = new DataApi("company");
        const [roleRes, companyRes] = await Promise.all([
          roleApi.fetchAll(),
          companyApi.fetchAll(),
        ]);
        const userRoles = roleRes?.data?.data || roleRes?.data || [];
        const companies = companyRes?.data?.data || companyRes?.data || [];
        setExternalData({
          userRoles: Array.isArray(userRoles) ? userRoles : [],
          companies: Array.isArray(companies) ? companies : [],
        });
      } catch (error) {
        console.error("Error fetching external data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchExternalData();
  }, []);

  const fields = {
    title: "firstname",
    subtitle: "email",
    status: "isactive",

    // ✅ DATABASE FIX: Guarantees the correct data is saved to the API
    customHandlers: {
      beforeSave: (formData) => {
        const cleanedData = { ...formData };
        if (cleanedData.country) {
          const cData = COUNTRY_TIMEZONE.find(c => c.countryName === cleanedData.country);
          if (cData) {
            cleanedData.country_code = cData.phoneCode;
            cleanedData.currency = cData.currency.code;
            if (cData.timezones && cData.timezones.length > 0) {
              cleanedData.time_zone = cData.timezones[0].zoneName;
            }
          }
        }
        return cleanedData;
      }
    },

    onChange: (key, value, formData, setFormData) => {
      if (key === "country") {
        const actualValue = value && typeof value === 'object' ? value.value : value;
        setCurrentCountry(actualValue || null);
        
        // This keeps Add User Modal working, even though Edit Mode ignores it
        if (typeof setFormData === 'function') {
           const cData = COUNTRY_TIMEZONE.find(c => c.countryName === actualValue);
           setFormData(prev => ({
             ...prev,
             country: actualValue,
             country_code: cData?.phoneCode || "",
             currency: cData?.currency?.code || "",
             time_zone: cData?.timezones?.[0]?.zoneName || ""
           }));
        }
      }
    },

    onLoad: (record, setFormValues) => {
      let countryToUse = record?.country;
      if (!countryToUse && companyInfo?.country) {
        countryToUse = companyInfo.country;
        if (setFormValues) {
          const cData = COUNTRY_TIMEZONE.find(c => c.countryName === countryToUse);
          if (cData) {
            setFormValues({
              ...record,
              country: cData.countryName,
              country_code: cData.phoneCode,
              currency: cData.currency.code,
              time_zone: cData.timezones.some(t => t.zoneName === companyInfo.time_zone)
                ? companyInfo.time_zone
                : cData.timezones[0].zoneName
            });
          }
        }
      }
      if (countryToUse) {
        setCurrentCountry(countryToUse);
      }
    },

  details: [
      { key: "firstname", label: "First Name", type: "text", required: true },
      { key: "lastname", label: "Last Name", type: "text", required: true },
      { key: "email", label: "Email", type: "text", required: true },
      {
        key: "country",
        label: "Country",
        type: "select",
        isSearchable: true,
        placeholder: "Select country...",
        options: COUNTRY_TIMEZONE.map(c => ({
          value: c.countryName,
          label: `${c.flag} ${c.countryName}`
        })),
        // ✅ THE SECRET: The 4 parameters must exactly match what ModuleDetail.js sends!
        onChange: (fieldKey, value, tempData, setTempData) => {
          const actualValue = value && typeof value === 'object' ? value.value : value;
          const selectedCountry = COUNTRY_TIMEZONE.find(c => c.countryName === actualValue);

          if (selectedCountry) {
            // Because we now have the proper setTempData function, this instantly updates the UI!
            setTempData(prev => ({
              ...prev,
              country: actualValue,
              country_code: selectedCountry.phoneCode,
              currency: selectedCountry.currency.code,
              time_zone: selectedCountry.timezones?.[0]?.zoneName || ""
            }));
          } else {
            setTempData(prev => ({
              ...prev,
              country: "",
              country_code: "",
              currency: "",
              time_zone: ""
            }));
          }
        },
        // This render is just to show the flag nicely when NOT in edit mode
        render: (value) => {
          if (value) {
            const country = COUNTRY_TIMEZONE.find(c => c.countryName.toLowerCase() === value.toLowerCase());
            return country ? `${country.flag} ${country.countryName}` : value;
          }
          return "N/A";
        }
      },
      {
        key: "country_code",
        label: "Country Code",
        type: "text",
        readOnly: true,
        disabled: true, // Field is locked
      },
      { key: "phone", label: "Phone", type: "text", maxLength: "10", required: true },
      {
        key: "currency",
        label: "Currency",
        type: "text",
        readOnly: true,
        disabled: true, // Field is locked
      },
      {
        key: "time_zone",
        label: "Time Zone",
        type: "text",
        readOnly: true,
        disabled: true, // Field is locked
      },
      {
        key: "userrole",
        label: "User Role",
        type: "select",
        readOnly: true,
        disabled: true, // User Role is firmly locked down
        required: true,
        options: externalData.userRoles.map(r => ({ value: r.id, label: r.role_name })),
      },
      {
        key: "library_member_type",
        label: "Gender",
        type: "select",
        isSearchable: true,
        required: false,
        options: library_member_type,
        colSize: 6,
      },
      {
        key: "isactive",
        label: "Status",
        type: "toggle",
        options: [
          { value: true, label: "Active" },
          { value: false, label: "Inactive" },
        ],
      },
    ],
    ...other,

    validationRules: (formData, allUsers = [], editingUser) => {
      const errors = [];
      if (!formData.firstname?.trim()) errors.push("First name is required");
      if (!formData.lastname?.trim()) errors.push("Last name is required");
      if (!formData.email?.trim()) errors.push("Email is required");
      if (!formData.country) errors.push("Country is required");
      if (!formData.userrole) errors.push("Role is required");

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (formData.email && !emailRegex.test(formData.email)) {
        errors.push("Invalid email format");
      }

      if (formData.phone) {
        const phoneRegex = /^[0-9]{10}$/;
        if (!phoneRegex.test(formData.phone)) {
          errors.push("Phone number must be exactly 10 digits");
        }
      }

      if (Array.isArray(allUsers)) {
        const duplicate = allUsers.find(
          user => user.email === formData.email && user.id !== editingUser?.id
        );
        if (duplicate) errors.push("Email already exists");
      }

      return errors;
    }
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <ModuleDetail
      moduleName="user"
      moduleApi="user"
      moduleLabel="User"
      icon="fa-solid fa-users"
      fields={fields}
      validationRules={fields.validationRules}
      externalData={externalData}
      timeZone={timeZone}
      permissions={permissions || {}}
      onTempDataChange={(tempData) => {
        if (tempData?.country && tempData.country !== currentCountry) {
          setCurrentCountry(tempData.country);
        }
      }}
    />
  );
};

export default UserDetail;